// Download.js - Gestion du téléchargement des documents DOCX
import axios from 'axios';
import { 
  Document, Packer, Paragraph, TextRun, AlignmentType, 
  BorderStyle, Table, TableRow, TableCell, WidthType,
  Header, PageNumber, ImageRun, VerticalAlign
} from 'docx';
import { saveAs } from 'file-saver';
import {
  buildPvBody,
  buildPvHeader,
  getPvReference,
  getPvSettings,
  PV_FONT_FAMILY,
  PV_FONT_SIZE,
  PV_LINE_SPACING,
  PV_LOGO_SVG,
  PV_PAGE,
} from '../../utils/pvDocument';

// Fonction pour convertir SVG texte en PNG base64 via canvas
const svgTextToPngBase64 = (svgText, width = 150, height = 150) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      
      // Fond blanc pour le logo
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      
      // Dessiner l'image SVG
      ctx.drawImage(img, 0, 0, width, height);
      
      // Convertir en base64
      const base64 = canvas.toDataURL('image/png').split(',')[1];
      resolve(base64);
    };
    
    img.onerror = (error) => {
      console.error('Erreur lors du chargement du logo:', error);
      reject(error);
    };
    
    // Créer un data URL à partir du SVG texte
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    img.src = url;
  });
};

// Fonction pour charger et convertir le logo SVG
const loadLogoPng = async () => {
  try {
    const base64 = await svgTextToPngBase64(PV_LOGO_SVG, 100, 100);
    return base64;
  } catch (error) {
    console.warn('Erreur conversion logo:', error);
    return null;
  }
};

// Fonction pour convertir base64 en Uint8Array pour docx ImageRun
const base64ToUint8Array = (base64) => {
  const binaryString = atob(base64);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes;
};

// Fonction pour charger une image depuis une URL et la convertir en Uint8Array
const loadImageFromUrl = async (url) => {
  try {
    console.log('Chargement image:', url);
    
    // Si c'est déjà une data URL base64
    if (url.startsWith('data:image')) {
      const base64Data = url.split(',')[1];
      let imageType = url.match(/data:image\/(\w+)/)?.[1] || 'png';
      if (imageType === 'jpg') imageType = 'jpeg';
      console.log('Image data URL détectée, type:', imageType);
      return {
        data: base64ToUint8Array(base64Data),
        type: imageType,
      };
    }
    
    // Extraire le type d'image depuis l'extension de fichier
    const getImageTypeFromUrl = (imgUrl) => {
      const extension = imgUrl.split('.').pop()?.toLowerCase().split('?')[0];
      const typeMap = {
        'jpg': 'jpg',
        'jpeg': 'jpg',
        'png': 'png',
        'gif': 'gif',
        'webp': 'webp',
        'bmp': 'bmp',
      };
      return typeMap[extension] || 'png';
    };

    const isAbsoluteUrl = (value) => {
      try {
        // URL lèvera une erreur si la chaîne n'est pas absolue
        // eslint-disable-next-line no-new
        new URL(value);
        return true;
      } catch {
        return false;
      }
    };

    const getBackendOrigin = () => {
      const { origin } = window.location;
      // En dev, le front est souvent en 3001 et le backend en 3000 : on bascule si besoin
      if (origin.includes(':3001')) {
        return origin.replace(':3001', ':3000');
      }
      return origin;
    };
    
    // Normaliser l'URL
    let absoluteUrl = url;
    const backendOrigin = getBackendOrigin();
    
    if (isAbsoluteUrl(url)) {
      // Ne pas réécrire une URL absolue (évite de casser http://localhost:3000/uploads/…)
      absoluteUrl = url;
    } else if (url.startsWith('//')) {
      absoluteUrl = `${window.location.protocol}${url}`;
    } else if (url.startsWith('/')) {
      absoluteUrl = `${backendOrigin}${url}`;
    } else if (url.includes('/uploads/')) {
      const uploadsMatch = url.match(/\/uploads\/[^?#]+/);
      if (uploadsMatch) {
        absoluteUrl = `${backendOrigin}${uploadsMatch[0]}`;
      }
    }
    
    console.log('Chargement depuis URL:', absoluteUrl);

    const headers = {};
    const token = typeof window !== 'undefined' ? sessionStorage.getItem('authToken') : null;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    // Charger l'image via fetch (avec auth si dispo)
    const response = await fetch(absoluteUrl, {
      method: 'GET',
      mode: 'cors',
      credentials: 'include',
      headers,
    });
    
    if (!response.ok) {
      console.error('Erreur HTTP lors du chargement de l\'image:', response.status, response.statusText);
      throw new Error(`Erreur HTTP: ${response.status}`);
    }
    
    const blob = await response.blob();
    
    // Vérifier que c'est bien une image
    if (!blob.type.startsWith('image/')) {
      console.error('Le fichier téléchargé n\'est pas une image:', blob.type);
      return null;
    }
    
    // Extraire le type d'image
    let imageType = getImageTypeFromUrl(url);
    
    // Vérifier aussi le Content-Type
    if (blob.type) {
      const typeMatch = blob.type.match(/image\/(\w+)/);
      if (typeMatch) {
        let detectedType = typeMatch[1];
        imageType = detectedType;
      }
    }
    
    if (imageType === 'jpg') {
      imageType = 'jpeg';
    }
    
    console.log('Image chargée, type:', imageType, 'taille:', blob.size, 'bytes');
    
    // Vérifier que l'image a un contenu
    if (blob.size === 0) {
      console.error('Image vide téléchargée');
      return null;
    }
    
    // Convertir le blob en ArrayBuffer puis en Uint8Array
    const arrayBuffer = await blob.arrayBuffer();
    return {
      data: new Uint8Array(arrayBuffer),
      type: imageType,
    };
  } catch (error) {
    console.error('Erreur lors du chargement de l\'image:', url, error);
    return null;
  }
};

/* ---------- Conversion des nœuds du PV (utils/pvDocument.js) en objets docx ---------- */

const ALIGNMENTS = {
  left: AlignmentType.LEFT,
  center: AlignmentType.CENTER,
  right: AlignmentType.RIGHT,
  justify: AlignmentType.JUSTIFIED,
};

const VERTICAL_ALIGNMENTS = {
  top: VerticalAlign.TOP,
  center: VerticalAlign.CENTER,
};

const NO_BORDER = { style: BorderStyle.NONE };
const CELL_BORDER = { style: BorderStyle.SINGLE, size: 1, color: '000000' };

const toTextRun = (run) => {
  if (run.break) {
    return new TextRun({ break: 1 });
  }

  const base = {
    font: run.font === false ? undefined : PV_FONT_FAMILY,
    size: run.size ?? PV_FONT_SIZE.body,
  };

  if (run.field) {
    return new TextRun({
      ...base,
      children: [run.field === 'page' ? PageNumber.CURRENT : PageNumber.TOTAL_PAGES],
    });
  }

  return new TextRun({
    ...base,
    text: run.text,
    bold: run.bold,
    italics: run.italics,
    underline: run.underline ? {} : undefined,
    strike: run.strike,
    color: run.color,
    shading: run.highlight ? { type: 'clear', fill: run.highlight } : undefined,
  });
};

const toBorders = (border) => {
  if (!border) return undefined;
  return Object.fromEntries(
    Object.entries(border).map(([side, value]) => [side, { ...value, style: BorderStyle.SINGLE }])
  );
};

const toParagraph = (node) => new Paragraph({
  alignment: ALIGNMENTS[node.alignment],
  spacing: node.spacing,
  indent: node.indent,
  border: toBorders(node.border),
  keepNext: node.keepNext,
  children: node.runs.map(toTextRun),
});

const toImageParagraph = async (node, logo) => {
  if (node.logo) {
    return new Paragraph({
      children: logo ? [
        new ImageRun({
          data: logo,
          transformation: { width: node.width, height: node.height },
          type: 'png',
        }),
      ] : [],
    });
  }

  const image = await loadImageFromUrl(node.src);
  if (image && image.data && image.data.length > 0) {
    return new Paragraph({
      alignment: ALIGNMENTS[node.alignment],
      children: [
        new ImageRun({
          data: image.data,
          transformation: { width: node.width, height: node.height },
          type: image.type === 'jpg' ? 'jpeg' : image.type,
        }),
      ],
    });
  }

  console.warn('Échec du chargement de l\'image:', node.src);
  return toParagraph({
    alignment: node.alignment,
    runs: [{ text: `[Image non disponible: ${node.src}]`, italics: true, color: '999999' }],
  });
};

const toTable = async (node, context) => {
  const side = node.borders ? CELL_BORDER : NO_BORDER;
  const cellBorders = { top: side, bottom: side, left: side, right: side };

  const rows = [];
  for (const row of node.rows) {
    const cells = [];
    for (const cell of row.cells) {
      const config = {
        children: await toDocxBlocks(cell.children, context),
        borders: cellBorders,
        verticalAlign: VERTICAL_ALIGNMENTS[cell.verticalAlign],
      };
      if (cell.width) {
        config.width = { size: cell.width, type: WidthType.PERCENTAGE };
      }
      cells.push(new TableCell(config));
    }
    rows.push(new TableRow({ children: cells }));
  }

  const options = {
    width: { size: 100, type: WidthType.PERCENTAGE },
    rows,
  };
  if (!node.borders) {
    options.borders = {
      top: NO_BORDER,
      bottom: NO_BORDER,
      left: NO_BORDER,
      right: NO_BORDER,
      insideHorizontal: NO_BORDER,
      insideVertical: NO_BORDER,
    };
  }
  if (node.columnWidths) {
    options.columnWidths = node.columnWidths;
  }
  return new Table(options);
};

// Les images sont chargées une à une, dans l'ordre du document
const toDocxBlocks = async (nodes, context) => {
  const blocks = [];
  for (const node of nodes) {
    if (node.type === 'table') {
      blocks.push(await toTable(node, context));
    } else if (node.type === 'image') {
      blocks.push(await toImageParagraph(node, context.logo));
    } else {
      blocks.push(toParagraph(node));
    }
  }
  return blocks;
};

/**
 * Télécharge le rapport au format DOCX
 * @param {Object} reportData - Les données du rapport
 * @returns {Promise<{success: boolean, message: string}>} - Résultat de l'opération
 */
export const downloadReportAsDocx = async (reportData) => {
  if (!reportData) {
    return { success: false, message: 'Données du rapport manquantes' };
  }
  
  try {
    const settingsResponse = await axios.get(`/api/settings`);
    const settings = settingsResponse.data;
    const pv = getPvSettings(settings);
    const reference = getPvReference(reportData, pv);

    let logo = null;
    try {
      const logoBase64 = await loadLogoPng();
      if (logoBase64) {
        logo = base64ToUint8Array(logoBase64);
      }
    } catch (logoError) {
      console.warn('Impossible de charger le logo, le document sera généré sans logo:', logoError);
    }

    const context = { logo };
    const children = await toDocxBlocks(buildPvBody(reportData, settings), context);
    // En-tête répété sur chaque page
    const header = new Header({
      children: await toDocxBlocks(buildPvHeader(reportData, settings), context),
    });

    const doc = new Document({
      creator: 'BlueNote',
      title: `PV-${reference}`,
      description: 'Document généré automatiquement par BlueNote',
      styles: {
        default: {
          document: {
            run: {
              font: PV_FONT_FAMILY,
              size: PV_FONT_SIZE.body,
            },
            paragraph: {
              spacing: {
                line: PV_LINE_SPACING,
              },
            },
          },
        },
      },
      sections: [
        {
          properties: {
            page: {
              size: { width: PV_PAGE.width, height: PV_PAGE.height },
              margin: PV_PAGE.margin,
            },
          },
          headers: {
            default: header,
          },
          children,
        },
      ],
    });
    
    const buffer = await Packer.toBlob(doc);
    const fileName = `PV_${pv.shortJudicialDistrict}_${reportData.indice || '__'}_${pv.judicialDivision}_${reportData.number}_${reportData.year}.docx`;
    saveAs(buffer, fileName);
    
    return { success: true, message: 'Document téléchargé' };
    
  } catch (error) {
    console.error('Erreur lors du téléchargement du document:', error);
    return { success: false, message: 'Erreur lors du téléchargement du document' };
  }
};

export default downloadReportAsDocx;
